import React, { useState, useEffect, useCallback } from 'react';
import { Form } from '@unform/web';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';

// servicos
import api from 'services/api';

// actions
import { show } from 'store/modules/dialog/actions';

// componentes
import Button from 'components/Button';
import Card from 'components/Card';
import Pagination from 'components/Pagination';
import Input from 'components/Input';
import Flag from 'components/Flag';
import Loader from 'components/ContentLoader/GenericLoader';

// estilos
import { Container, Scroll } from './styles';
import { Table, Align, Td } from 'styles/global';

// icons
import { FiSearch, FiSkipForward, FiFeather } from 'react-icons/fi';
import { Categoria, StatusBaixa, StatusCaption } from 'utils/variables';
import AppError from 'utils/AppError';
import { Midia } from 'pages/@types';
import Tooltip from 'components/Tooltip';

export interface IMidiaItem {
  id: number;
  filename: string;
}
export default function ClosedCaption() {
  /**
   * USESTATE
   */
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Midia>({} as Midia);
  const [midias, setMidias] = useState<Midia[]>([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState('');

  // react-router-dom
  const navigate = useNavigate();

  /*
   * REDUX
   */
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * USECALLBACKS
   */

  const handleCaptionByPass = useCallback(
    midia => {
      try {
        const optionBase = {
          type: 'question',
          text: `Pular correção de Closed Caption de ${midia.filename} ?`,
          confirmButtonText: 'CONFIRMAR',
          callback: {
            url: `/midias/${midia.id}`,
            method: api.put,
            data: { captionbypass: true },
            action: 'skipcaption',
          },
          haveCallback: true,
          successMessage: 'Edição de CC cancelada!',
          cancelButtonText: 'NÃO',
          showCancelButton: false,
          showConfirmButton: true,
          showCloseButton: true,
        };
        setSelected(midia);
        dispatch(show(optionBase));
      } catch (err) {}
    },
    [dispatch],
  );
  /**
   * USEEFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function buscaMidiasClosedCaption() {
      setLoading(true);
      try {
        const response = await api.get(`/midia/closedcaption`, {
          signal: controller.signal,
          params: {
            page,
            ...(filtro && { search: filtro }),
          },
        });
        if (response && response.data) {
          setMidias(
            response.data.midias.map((midia: Midia) => {
              return {
                ...midia,
                created_at: format(
                  parseISO(midia.created_at),
                  "dd 'de' MMMM', às' H:mm'	",
                  {
                    locale: pt,
                  },
                ),
              };
            }),
          );
          setPages(response.data.pages);
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
          console.log(error);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    }
    buscaMidiasClosedCaption();

    return () => {
      controller.abort();
    };
  }, [filtro, page]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'skipcaption' && selected) {
        setMidias(prevState =>
          prevState.map(p =>
            p.id === selected.id
              ? {
                  ...p,
                  statuscaption:
                    callbackDialog.success.data.midia.statuscaption,
                }
              : p,
          ),
        );
      }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <Card>
        <header>
          <h4>LISTA DE MIDIAS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <div>
            <Form
              onSubmit={e => {
                setPage(1);
                setFiltro(e.searchLocal);
              }}
            >
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
            </Form>
          </div>
        </header>
        <Scroll>
          {loading ? (
            <Loader type="" />
          ) : !midias || (midias && midias.length === 0) ? (
            <p>Nenhuma mídia encontrada</p>
          ) : (
            midias &&
            midias.length && (
              <Table>
                <thead>
                  <tr>
                    <th>FILENAME</th>
                    <th style={{ width: '150px' }}>CATEGORIA</th>
                    <th style={{ width: '150px' }}>STATUS CC</th>
                    <th style={{ width: '150px' }}>STATUS BAIXA</th>
                    <th style={{ width: '225px' }}>DATA DE CRIAÇÃO</th>
                    <th style={{ width: '65px' }}>AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {!!midias.length &&
                    midias.map((midia: Midia) => (
                      <tr key={midia.id}>
                        <Td>{midia.filename}</Td>
                        <Td>
                          <Flag name={midia.categoria} type={midia.categoria} />
                        </Td>
                        <Td>
                          <Flag
                            name={midia.statuscaption}
                            type={midia.statuscaption}
                          />
                        </Td>
                        <Td>
                          <Flag
                            name={midia.statusbaixa}
                            type={midia.statusbaixa}
                          />
                        </Td>
                        <Td>{midia.created_at}</Td>
                        <td>
                          <Align>
                            <Button
                              id={`id_editar_${midia.id}`}
                              fill
                              round
                              color="font"
                              onClick={() =>
                                navigate(
                                  `/closedcaptioneditor/${midia.id}/optativos`,
                                )
                              }
                              disabled={
                                midia.statusbaixa !== StatusBaixa.PROCESSADO ||
                                midia.statuscaption ===
                                  StatusCaption.ENCAPSULADA ||
                                midia.statuscaption === StatusCaption.EDITADA
                              }
                            >
                              <FiFeather />
                            </Button>
                            <Tooltip target={`id_editar_${midia.id}`}>
                              EDITAR
                            </Tooltip>
                            <Button
                              id={`id_skip_${midia.id}`}
                              fill
                              round
                              color="font"
                              onClick={() => handleCaptionByPass(midia)}
                              disabled={
                                midia.statuscaption ===
                                  StatusCaption.ENCAPSULADA ||
                                midia.statuscaption === StatusCaption.EDITADA ||
                                midia.categoria === Categoria.OPTATIVO
                              }
                            >
                              <FiSkipForward />
                            </Button>
                            <Tooltip
                              target={`id_skip_${midia.id}`}
                              position="left"
                            >
                              PULAR EDIÇÃO DE CC
                            </Tooltip>
                          </Align>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )
          )}
        </Scroll>
      </Card>
    </Container>
  );
}
